import { useMemo, useState } from "react";
import type { AbEntity, AbGenre, AbMood } from "#shared/schemas/abEnrich.ts";
import { JsonTree } from "@web/features/settings/JsonTree.tsx";
import { formatDateTime } from "@web/lib/format.ts";
import { cn } from "@web/lib/utils.ts";

function abPage(mbid: string): string {
  return `https://acousticbrainz.org/${mbid}`;
}

function pct(p: number): string {
  return `${Math.round(p * 100)}%`;
}

function humanizeLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bHiphop\b/i, "Hip-hop")
    .replace(/\bRaphiphop\b/i, "Rap / hip-hop")
    .replace(/\bFunksoulrnb\b/i, "Funk / soul / R&B")
    .replace(/\bFolkcountry\b/i, "Folk / country");
}

/** Binary AB moods whose winning class is the negative side. */
function isNegativeMoodValue(value: string): boolean {
  const v = value.trim().toLowerCase();
  return v.startsWith("not_") || v.startsWith("not ");
}

function isMirex(mood: AbMood): boolean {
  return mood.id === "moods_mirex" || mood.label.toLowerCase() === "mirex";
}

const PRIMARY_GENRE_ORDER = ["Rosamerica", "Dortmund", "Electronic", "Tzanetakis"];

function pickPrimaryGenre(genres: AbGenre[]): AbGenre | null {
  if (genres.length === 0) return null;
  for (const model of PRIMARY_GENRE_ORDER) {
    const hit = genres.find((g) => g.model === model);
    if (hit) return hit;
  }
  return [...genres].sort((a, b) => b.probability - a.probability)[0] ?? null;
}

function Chip({ children, title }: { children: string; title?: string }) {
  return (
    <span
      title={title}
      className="rounded bg-panel-elevated px-1.5 py-0.5 text-xs text-text"
    >
      {children}
    </span>
  );
}

/**
 * Browse-first AcousticBrainz block: BPM/key, positive moods, one primary genre.
 * Full classifier dump + raw JSON stay collapsed.
 */
export function AbPrettyView({ entity }: { entity: AbEntity }) {
  const [rawOpen, setRawOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const p = entity.parsed;

  const browse = useMemo(() => {
    const positiveMoods = p.moods
      .filter((m) => !isMirex(m) && !isNegativeMoodValue(m.value))
      .sort((a, b) => b.probability - a.probability);
    const primaryGenre = pickPrimaryGenre(p.genres);
    return { positiveMoods, primaryGenre };
  }, [p.moods, p.genres]);

  if (entity.fetch_status === "not_found") {
    return (
      <section className="space-y-2">
        <p className="text-xs font-semibold tracking-wider text-faint uppercase">
          AcousticBrainz
        </p>
        <p className="text-sm text-muted">
          No acoustic data for this recording (archive closed 2022 — missing is
          normal for newer tracks).
        </p>
      </section>
    );
  }

  if (entity.fetch_status === "failed") {
    return (
      <section className="space-y-2">
        <p className="text-xs font-semibold tracking-wider text-faint uppercase">
          AcousticBrainz
        </p>
        <p className="text-sm text-muted">
          Fetch failed{entity.error ? `: ${entity.error}` : "."}
        </p>
      </section>
    );
  }

  const keyLine =
    p.key && p.scale ? `${p.key} ${p.scale}` : (p.key ?? p.scale ?? null);

  const heroParts: string[] = [];
  if (p.bpm != null) heroParts.push(`${p.bpm} BPM`);
  if (keyLine) heroParts.push(keyLine);

  const empty =
    heroParts.length === 0 &&
    browse.positiveMoods.length === 0 &&
    !browse.primaryGenre;

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold tracking-wider text-faint uppercase">
            AcousticBrainz
          </p>
          <a
            href={abPage(entity.recording_mbid)}
            target="_blank"
            rel="noreferrer"
            className="mt-1 block truncate text-sm text-accent hover:underline"
          >
            {entity.recording_mbid}
          </a>
          <p className="mt-1 text-xs text-muted">
            {entity.fetch_status}
            {entity.fetched_at ? ` · ${formatDateTime(entity.fetched_at)}` : ""}
          </p>
        </div>
      </div>

      <div className="rounded-md border border-line bg-panel px-3 py-3 space-y-3">
        {heroParts.length > 0 && (
          <p className="text-base font-semibold text-text">{heroParts.join(" · ")}</p>
        )}

        {browse.positiveMoods.length > 0 && (
          <div>
            <p className="mb-1.5 text-xs text-faint">Feels like</p>
            <div className="flex flex-wrap gap-1.5">
              {browse.positiveMoods.map((m) => (
                <Chip key={m.id} title={`${m.label} classifier · ${pct(m.probability)}`}>
                  {`${humanizeLabel(m.value)} · ${pct(m.probability)}`}
                </Chip>
              ))}
            </div>
          </div>
        )}

        {browse.primaryGenre && (
          <div>
            <p className="mb-1 text-xs text-faint">Genre</p>
            <p className="text-sm text-text">
              {humanizeLabel(browse.primaryGenre.value)}
              <span className="ml-1.5 text-xs text-faint">
                {browse.primaryGenre.model} · {pct(browse.primaryGenre.probability)}
              </span>
            </p>
          </div>
        )}

        {empty && (
          <p className="text-sm text-muted">Stored, but no browseable fields.</p>
        )}
      </div>

      <div className="rounded-md border border-line bg-panel">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
          onClick={() => setMoreOpen((v) => !v)}
        >
          <span className="text-xs font-semibold tracking-wider text-faint uppercase">
            All classifiers
          </span>
          <span className="text-xs text-muted">{moreOpen ? "Hide" : "Show"}</span>
        </button>
        {moreOpen && (
          <div className="space-y-3 border-t border-line px-3 py-2">
            {p.moods.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs text-faint">Moods</p>
                <ul className="divide-y divide-line">
                  {p.moods.map((m) => (
                    <li
                      key={m.id}
                      className={cn(
                        "flex items-baseline justify-between gap-3 py-1.5 text-sm",
                        isNegativeMoodValue(m.value) && "opacity-60",
                      )}
                    >
                      <span className="text-muted">{m.label}</span>
                      <span className="tabular-nums text-text">
                        {humanizeLabel(m.value)}
                        <span className="ml-1.5 text-xs text-faint">
                          {pct(m.probability)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {p.genres.length > 0 && (
              <div>
                <p className="mb-1.5 text-xs text-faint">Genre models</p>
                <ul className="divide-y divide-line">
                  {p.genres.map((g) => (
                    <li
                      key={g.model}
                      className="flex items-baseline justify-between gap-3 py-1.5 text-sm"
                    >
                      <span className="text-muted">{g.model}</span>
                      <span className="tabular-nums text-text">
                        {humanizeLabel(g.value)}
                        <span className="ml-1.5 text-xs text-faint">
                          {pct(g.probability)}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-md border border-line bg-panel">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
          onClick={() => setRawOpen((v) => !v)}
        >
          <span className="text-xs font-semibold tracking-wider text-faint uppercase">
            Raw AcousticBrainz JSON
          </span>
          <span className="text-xs text-muted">{rawOpen ? "Hide" : "Show"}</span>
        </button>
        {rawOpen && (
          <div className="max-h-[28rem] space-y-3 overflow-auto border-t border-line px-3 py-2">
            {entity.low_level_json != null && (
              <div>
                <p className="mb-1 text-xs text-faint">low-level</p>
                <JsonTree value={entity.low_level_json} name="low-level" />
              </div>
            )}
            {entity.high_level_json != null && (
              <div>
                <p className="mb-1 text-xs text-faint">high-level</p>
                <JsonTree value={entity.high_level_json} name="high-level" />
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
